import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Text } from "@core/index";
import bgImage from "@packages/assets/testimagegrocery.png";
import { MicOff, Mic, PhoneOff, AlertTriangle, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  Room,
  RoomEvent,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Track,
} from "livekit-client";
import { useCreateSession } from "@packages/api/madlenAPI";

const PublicSpokenDialoguePage = () => {
  const { t } = useTranslation();
  const params = useParams();
  const publicCode = (params.code as string | undefined) || "";
  const [isMuted, setIsMuted] = useState(false);
  const [showEndCallPopup, setShowEndCallPopup] = useState(false);
  const [showNameModal, setShowNameModal] = useState(true);
  const [studentName, setStudentName] = useState("");
  const [room, setRoom] = useState<Room | null>(null);
  const [dynamicBg, setDynamicBg] = useState<string | null>(null);
  const [livekitUrl, setLivekitUrl] = useState<string | undefined>(undefined);
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const [dialoguePhase, setDialoguePhase] = useState<
    "active" | "loading" | "results"
  >("active");
  const [evaluationData, setEvaluationData] = useState<{
    score: number;
    strengths: string[];
    improvements: string[];
    feedback: string;
    category_scores: Record<string, number>;
  } | null>(null);

  // Timer state: elapsed seconds and a ref to the interval id
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const timerRef = useRef<number | null>(null);

  // Start the timer when the LiveKit room is connected (room != null) and dialogue is active.
  // Stop the timer whenever the dialoguePhase is not 'active' or when the room disconnects.
  useEffect(() => {
    if (room && dialoguePhase === "active") {
      if (timerRef.current == null) {
        timerRef.current = window.setInterval(() => {
          setElapsedSeconds((s) => s + 1);
        }, 1000);
      }
    } else {
      if (timerRef.current != null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      // Reset the counter when the room is no longer connected
      if (!room) {
        setElapsedSeconds(0);
      }
    }

    return () => {
      if (timerRef.current != null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [room, dialoguePhase]);

  const audioContainerRef = useRef<HTMLDivElement | null>(null);

  // Helper to attach/detach remote audio tracks
  const handleTrackSubscribed = (
    track: RemoteTrack,
    _publication: RemoteTrackPublication,
    _participant: RemoteParticipant,
  ) => {
    if (track.kind === Track.Kind.Audio) {
      const el = track.attach();
      el.autoplay = true;
      el.hidden = true;
      audioContainerRef.current?.appendChild(el);
    }
  };

  const handleTrackUnsubscribed = (track: RemoteTrack) => {
    track.detach().forEach((el) => el.remove());
  };

  const disconnectRoom = () => {
    try {
      room?.disconnect();
    } catch (_) {
      // noop
    } finally {
      setRoom(null);
    }
  };

  const connectToRoom = async (
    overrideUrl?: string,
    overrideToken?: string,
  ) => {
    try {
      const effectiveUrl = overrideUrl || livekitUrl;
      const effectiveToken = overrideToken || accessToken;
      if (!effectiveUrl || !effectiveToken) {
        // eslint-disable-next-line no-console
        console.warn("LiveKit credentials are missing");
        return;
      }

      const lkRoom = new Room();
      await lkRoom.connect(effectiveUrl, effectiveToken);

      // Wire room events
      lkRoom.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      lkRoom.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
      lkRoom.on(RoomEvent.Disconnected, () => {
        setRoom(null);
      });

      // Listen for agent messages
      lkRoom.registerTextStreamHandler(
        "agent_to_client_messages",
        async (reader) => {
          try {
            const messageText = await reader.readAll();
            const messageData = JSON.parse(messageText);

            if (messageData.message === "dialogue_ended") {
              // Mute microphone and show loading
              setIsMuted(true);
              await lkRoom.localParticipant.setMicrophoneEnabled(false);
              setDialoguePhase("loading");
            } else if (messageData.message === "evaluation_complete") {
              // Show results with evaluation data
              if (messageData.data) {
                setEvaluationData(messageData.data);
                setDialoguePhase("results");
              }
            }
          } catch (error) {
            console.warn("Failed to parse agent message:", error);
          }
        },
      );

      setRoom(lkRoom);
      setIsMuted(false);
      // publish mic by default
      try {
        await lkRoom.localParticipant.setMicrophoneEnabled(true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Microphone enable failed:", e);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("LiveKit connect error", err);
    } finally {
      // no-op
    }
  };

  const handleEndCall = () => {
    setShowEndCallPopup(true);
  };

  const confirmEndCall = () => {
    // Handle actual end call logic here
    setShowEndCallPopup(false);
  };

  const cancelEndCall = () => {
    setShowEndCallPopup(false);
  };

  const createSessionMutation = useCreateSession();
  const { mutateAsync: createSessionMutate } = createSessionMutation;

  const handleNameSubmit = async () => {
    if (!studentName.trim() || !publicCode) return;
    try {
      // Create session on backend to receive token, image, etc.
      const res = await createSessionMutate({
        publicCode,
        data: {
          participantName: studentName.trim(),
          participantIdentity: studentName.trim(),
        },
      });
      const payload = (res as any) || {};
      const token = payload?.accessToken as string | undefined;
      setAccessToken(token);
      const envUrl = (import.meta as any)?.env?.VITE_LIVEKIT_URL as
        | string
        | undefined;
      const url =
        envUrl ||
        (window as any).__LIVEKIT_URL__ ||
        livekitUrl ||
        "wss://madlen-spoken-dialogue-dev-9ne42nkq.livekit.cloud";
      setLivekitUrl(url);
      if (payload?.imageUrl) {
        setDynamicBg(payload.imageUrl as string);
      }
      setShowNameModal(false);
      // Avoid race with async state updates by passing credentials directly
      void connectToRoom(url, token);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("create public session failed", e);
    }
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (room) {
        try {
          room.disconnect();
        } catch (_) {
          // noop
        }
      }
    };
  }, [room]);

  if (showNameModal) {
    return (
      <div className="min-h-screen w-full relative overflow-hidden bg-gray-900">
        {/* Simple background without image */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-800 via-gray-900 to-black" />

        {/* Name Input Modal */}
        <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="rounded-3xl bg-black/30 backdrop-blur-2xl border border-white/15 shadow-2xl overflow-hidden p-8 text-center">
              {/* Avatar */}
              <div className="mx-auto h-20 w-20 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-2xl text-white mb-6">
                <User className="w-10 h-10" />
              </div>

              {/* Title */}
              <Text
                variant="h2"
                className="text-xl font-semibold text-white/90 mb-2"
              >
                Welcome
              </Text>
              <Text variant="p" className="text-white/70 text-sm mb-6">
                Please enter your name to begin
              </Text>

              {/* Name Input */}
              <div className="mb-6">
                <input
                  type="text"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleNameSubmit();
                    }
                  }}
                />
              </div>

              {/* Start Button */}
              <button
                type="button"
                onClick={handleNameSubmit}
                disabled={!studentName.trim()}
                className="w-full px-6 py-3 rounded-xl bg-white hover:bg-white disabled:bg-gray-500 disabled:cursor-not-allowed text-black font-medium transition-colors hover:text-black"
              >
                Start Conversation
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full relative overflow-hidden">
      {/* Themed background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${dynamicBg || bgImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />

      {/* Foreground content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
        {!showEndCallPopup && (
          <div className="w-full max-w-md">
            {/* Loading state */}
            {dialoguePhase === "loading" && (
              <div className="rounded-3xl bg-black/30 backdrop-blur-2xl border border-white/15 shadow-2xl overflow-hidden p-8 text-center">
                <div className="mx-auto h-28 w-28 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-4xl text-white mb-6">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
                </div>
                <Text
                  variant="h2"
                  className="text-lg font-semibold text-white/90 mb-2"
                >
                  Dialogue Complete!
                </Text>
                <Text variant="p" className="text-white/70 text-sm">
                  You have completed the dialogue, your score is being
                  calculated.
                </Text>
              </div>
            )}

            {/* Active call surface */}
            {dialoguePhase === "active" && (
              <div className="rounded-3xl bg-black/30 backdrop-blur-2xl border border-white/15 shadow-2xl overflow-hidden p-8 text-center">
                {/* Avatar */}
                <div className="mx-auto h-28 w-28 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-4xl text-white">
                  <svg
                    fill="#ffffff"
                    width="64px"
                    height="64px"
                    viewBox="0 0 512 512"
                    id="icons"
                    xmlns="http://www.w3.org/2000/svg"
                    stroke="#ffffff"
                  >
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      <path d="M208,512,155.62,372.38,16,320l139.62-52.38L208,128l52.38,139.62L400,320,260.38,372.38Z"></path>
                      <path d="M88,176,64.43,111.57,0,88,64.43,64.43,88,0l23.57,64.43L176,88l-64.43,23.57Z"></path>
                      <path d="M400,256l-31.11-80.89L288,144l80.89-31.11L400,32l31.11,80.89L512,144l-80.89,31.11Z"></path>
                    </g>
                  </svg>
                </div>
                {/* Title and timer */}
                <div className="mt-5">
                  <Text
                    variant="h2"
                    className="text-lg font-semibold text-white/90"
                  >
                    {t("spokenDialogue.detail.title")}
                  </Text>
                  <div className="mt-2 text-3xl font-mono tracking-wider text-white">
                    {(() => {
                      const mins = Math.floor(elapsedSeconds / 60);
                      const secs = elapsedSeconds % 60;
                      const formattedMins = mins < 10 ? `0${mins}` : `${mins}`;
                      const formattedSecs = secs < 10 ? `0${secs}` : `${secs}`;
                      return `${formattedMins}:${formattedSecs}`;
                    })()}
                  </div>
                  {studentName && (
                    <div className="mt-2 text-sm text-white/70">
                      Hello, {studentName}!
                    </div>
                  )}
                </div>

                {/* Controls */}
                <div className="mt-8 flex items-center justify-center gap-4">
                  <button
                    type="button"
                    aria-label="toggle mute"
                    onClick={() => {
                      const nextMuted = !isMuted;
                      setIsMuted(nextMuted);
                      if (room) {
                        void room.localParticipant.setMicrophoneEnabled(
                          !nextMuted,
                        );
                      }
                    }}
                    className="h-12 px-6 rounded-full bg-white/15 hover:bg-white/25 text-white border border-white/25 flex items-center gap-3"
                  >
                    {isMuted ? (
                      <MicOff className="w-6 h-6 flex-shrink-0" />
                    ) : (
                      <Mic className="w-6 h-6 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium">
                      {isMuted ? "Unmute" : "Mute"}
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label="end"
                    onClick={handleEndCall}
                    className="h-12 w-12 rounded-full bg-rose-600 hover:bg-rose-700 text-white shadow-lg flex items-center justify-center flex-shrink-0"
                  >
                    <PhoneOff className="w-6 h-6" />
                  </button>
                </div>
              </div>
            )}

            {/* Results state */}
            {dialoguePhase === "results" && evaluationData && (
              <div className="rounded-3xl bg-black/30 backdrop-blur-2xl border border-white/15 shadow-2xl overflow-hidden p-8">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="mx-auto h-20 w-20 rounded-full bg-green-500/20 border border-green-400/30 flex items-center justify-center text-green-400 mb-4">
                    <svg
                      className="w-10 h-10"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <Text
                    variant="h2"
                    className="text-xl font-semibold text-white/90 mb-2"
                  >
                    Dialogue Complete!
                  </Text>
                  <Text variant="p" className="text-white/70 text-sm">
                    Here are your results
                  </Text>
                </div>

                {/* Score */}
                <div className="bg-white/10 rounded-2xl p-6 mb-6 text-center">
                  <Text
                    variant="h1"
                    className="text-4xl font-bold text-white mb-2"
                  >
                    {evaluationData.score}
                  </Text>
                  <Text variant="p" className="text-white/70 text-sm">
                    Overall Score
                  </Text>
                </div>

                {/* Category Scores */}
                <div className="space-y-3 mb-6">
                  <Text variant="h3" className="text-white/90 font-medium mb-3">
                    Category Breakdown
                  </Text>
                  {Object.entries(evaluationData.category_scores).map(
                    ([category, score]) => (
                      <div
                        key={category}
                        className="flex justify-between items-center"
                      >
                        <Text
                          variant="p"
                          className="text-white/80 text-sm capitalize"
                        >
                          {category.replace(/_/g, " ")}
                        </Text>
                        <Text variant="p" className="text-white font-medium">
                          {score}/20
                        </Text>
                      </div>
                    ),
                  )}
                </div>

                {/* Strengths */}
                {evaluationData.strengths.length > 0 && (
                  <div className="mb-4">
                    <Text
                      variant="h3"
                      className="text-green-400 font-medium mb-2 text-sm"
                    >
                      ✓ Strengths
                    </Text>
                    <ul className="space-y-1">
                      {evaluationData.strengths.map((strength, index) => (
                        <li key={index} className="text-white/80 text-xs">
                          • {strength}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Improvements */}
                {evaluationData.improvements.length > 0 && (
                  <div className="mb-4">
                    <Text
                      variant="h3"
                      className="text-yellow-400 font-medium mb-2 text-sm"
                    >
                      ⚡ Areas for Improvement
                    </Text>
                    <ul className="space-y-1">
                      {evaluationData.improvements.map((improvement, index) => (
                        <li key={index} className="text-white/80 text-xs">
                          • {improvement}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Footer note for students */}
            {dialoguePhase === "active" && (
              <div className="mt-4 ml-12 mr-12 text-center">
                <Text variant="p" className="text-xs text-white/80">
                  {t("spokenDialogue.public.footerNote")}
                </Text>
              </div>
            )}
          </div>
        )}
      </div>

      {/* End Call Confirmation Popup */}
      {showEndCallPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={cancelEndCall}
          />
          <div className="relative w-full max-w-md">
            <div className="rounded-3xl bg-black/30 backdrop-blur-2xl border border-white/15 shadow-2xl overflow-hidden p-8 text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-rose-100/20 border border-rose-200/30 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-rose-400" />
                </div>
              </div>
              <Text
                variant="h3"
                className="text-xl font-semibold text-white/90 mb-2"
              >
                End Call?
              </Text>
              <Text variant="p" className="text-white/70 text-sm mb-6">
                You will lose all your evaluation data. Are you sure you want to
                end the call?
              </Text>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={cancelEndCall}
                  className="flex-1 px-4 py-3 rounded-xl border border-white/20 text-white hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    disconnectRoom();
                    confirmEndCall();
                  }}
                  className="flex-1 px-4 py-3 rounded-xl bg-white text-black font-medium hover:bg-white/90 transition-colors"
                >
                  End Call
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Hidden container to attach remote audio elements */}
      <div ref={audioContainerRef} style={{ display: "none" }} />
    </div>
  );
};

export default PublicSpokenDialoguePage;
